<template>
  <div class="tbb-enterprise-form w-full">
      <!-- INITIAL/FORM STATE -->
      <div
        v-if="!submitted"
        class="relative w-full mx-auto max-w-xl p-8 md:py-24">
        <div class="mt-6">
          <h3>Organization/Property Information</h3>

          <div>
            <div>
              <label>
                Organization/Property Name *:
                <input v-model="contract.name" type="text" required/>
              </label>
            </div>

            <div class="mt-4">
              <label>
              Country *:
              <select v-model="contract.country" class="w-full" style="appearance: auto;">
                <option value="US">United States</option>
                <option v-for="country in Object.entries(countries)" :value="country[0]" v-if="country[0] !== 'US'">{{ country[1] }}</option>
              </select>
              </label>
            </div>

            <div class="mt-4">
              <label>
                Street Address *:
                <input v-model="contract.street" type="text" required />
              </label>
            </div>

            <div class="mt-4">
              <label>
                City *:
                <input v-model="contract.city" type="text" required/>
              </label>
            </div>

            <div class="mt-4">
              <label>
                State:
                <input v-model="contract.state" type="text" />
              </label>
            </div>

            <div class="mt-4">
              <label>
                Postal Code:
                <input v-model="contract.zipcode" type="text" />
              </label>
            </div>
          </div>

          <div class="mt-12">
            <h3>On-site Administrator Information</h3>

            <div class="mt-4">
              <label>
                First Name *:
                <input v-model="contract.adminFname" type="text" />
              </label>
            </div>

            <div class="mt-4">
              <label>
                Last Name *:
                <input v-model="contract.adminLname" type="text" />
              </label>
            </div>

            <div class="mt-4">
              <label>
                Email Address *:
                <input v-model="contract.adminEmail" type="email" />
              </label>
            </div>

            <div class="mt-4">
              <label>
                Phone Number *:
                <input v-model="contract.adminPhone" type="tel" />
              </label>
            </div>

            <div class="mt-4">
              <label>
                Number of licenses *:
                <select v-model="contract.seats" class="w-full" style="appearance: auto;">
                  <!-- <option value="small-business">Small Business: Up to 3 Licenses/Location ($999/yr)</option> -->
                  <option value="enterprise">Enterprise: Up to 5 Licenses/Location ($2,499/yr)</option>
                  <option value="high-school">High School: Unlimited Users/School ($999/yr)</option>
                  <option value="custom-school">Post-Secondary School: Contact us for a quote.</option>
                  <option value="custom">Larger Teams/Organizations: Contact us for a quote.</option>
                </select>
              </label>
            </div>
          </div>
        </div>

        <div class="mt-12" v-if="['custom-school','custom'].includes(contract.seats)">
          <h3>Custom Quote</h3>

          <div class="mt-4">
            For larger teams or multiple locations or post-secondary education, please contact us for a personalized quote, including volume discounts.
          </div>
        </div>

        <div v-show="!['custom-school','custom'].includes(contract.seats)">

          <div class="mt-12">
            <label>
              Coupon Code:
              <input class="w-full" v-model="coupon" v-on:change="handleCouponChange" type="text" />
            </label>
          </div>
          <div v-if="couponLoading" class="enterprise-coupon-loading-indicator">
            <p class="font-bold">
              Checking coupon...
            </p>
          </div>
          <div v-if="couponMatch.valid" class="mt-4">
            <p class="font-bold">
              {{ couponMatch.name }}<br />
              {{ couponMatch.percent_off ? `-${couponMatch.percent_off}%` : couponMatch.amount_off ? `-$${couponMatch.amount_off / 100}` : '' }}
            </p>
          </div>

          <div class="mt-12">
            <h4 v-if="price === originalPrice">Price: ${{ price }}</h4>
            <h4 v-if="price !== originalPrice">Price: <span class="line-through font-semibold pr-1 text-red-500">${{ originalPrice }}</span>${{ price }}</h4>
          </div>

          <h3 class="mt-12">Payment Information</h3>
          <p>(for one time payment)</p>

          <div class="mt-4">
            <label>
              Cardholder's Name *:
              <input v-model="contract.billingName" type="text" />
            </label>
          </div>

          <div class="mt-4">
            <label>
              Cardholder's Email *:
              <input v-model="contract.billingEmail" type="email" />
            </label>
          </div>

          <div class="mt-4">
            <label>
              Card Number *:
              <div style="background: white; padding: 0.75rem; border-radius: 4px; border: solid 2px #DFE0E3;">
                <div id="stripe-card-element"></div>
              </div>
            </label>
          </div>

          <div class="mt-4">
            <label>
              Credit Card Billing Postal Code *:
              <input class="w-full" v-model="contract.billingZipcode" type="text" />
            </label>
          </div>

          <div class="mt-4">
            <label>
              Cardholder Contact Phone Number *:
              <input v-model="contract.billingPhone" type="tel" />
            </label>
          </div>
        </div>

        <div class="mt-12">
          <label>
            Additional information:
            <textarea v-model="contract.additionalInformation" class="w-full" style="min-height: 5em;" rows="4"></textarea>
          </label>
        </div>

        <!-- Terms And Conditions -->
        <div class="mt-12">
          <label>
              <input type="checkbox" v-model="acceptTerms">
              I Accept <a href="/terms" target="_blank"> The Terms Of Service </a>
          </label>
        </div>

        <div class="mt-12">
          <button v-on:click="submit" class="btn btn-primary" :disabled="couponLoading">
            Submit
          </button>
        </div>
      </div>

      <!-- LOADING STATE -->
      <div v-if="loading" class="enterprise-loading-indicator">
        <div class="bg-gray-500 opacity-50 fixed top-0 bottom-0 left-0 right-0"></div>
        <div class="text-4xl font-bold fixed top-0 bottom-0 left-0 right-0 flex justify-center items-center">
          Loading ... Please Wait
        </div>
      </div>

      <!-- THANKS STATE -->
      <div v-if="submitted" class="enterprise-success-indicator bg-gray-100 p-8 md:p-12">
        <div v-if="['custom-school','custom'].includes(contract.seats)">
          <p>Thank you for contacting us.</p>
          <p>An email will be sent to your On-site Administrator to further discuss your organization’s needs.</p>
        </div>
        <div v-if="!['custom-school','custom'].includes(contract.seats)">
          <p>Thank you for choosing The Butter Book as your pastry education resource.</p>
          <p>The On-site Administrator you designated during purchase can expect a follow-up email from The Butter Book team within 24 business hours containing detailed instructions on how to grant access to their team.</p>
        </div>
      </div>
    </div>
</template>

<script>
import axios from "axios";
import { debounce } from "lodash";
import countries from '../../../countries.json'

const exposedCouponErrors = [
  'Coupon does not apply to product',
  'Coupon is expired',
  'Coupon does not exist',
]

function checkServerError(response) {
  if (response.status !== 200) {
    throw new Error(response.statusText);
  }

  return response;
}

export default {
  mixins: [
    require('../../spark/mixins/stripe')
  ],

  props: [],

  mounted() {
    this.cardElement = this.createCardElement('#stripe-card-element', {})
  },

  data() {
    return {
      acceptTerms: false,
      couponLoading: false,
      contract: {
        adminFname: '',
        adminLname: '',
        adminEmail: '',
        adminPhone: '',
        additionalInformation: '',
        billingName: '',
        billingEmail: '',
        billingZipcode: '',
        billingPhone: '',
        city: '',
        country: 'US',
        name: '',
        seats: 'enterprise',
        state: '',
        street: '',
        zipcode: '',
      },
      countries,
      coupon: '',
      couponMatch: {
        valid: false,
        name: '',
        percent_off: 0,
        amount_off: 0,
      },
      loading: false,
      originalPrice: 999,
      price: 999,
      submitted: false,
    }
  },
  
  watch: {
    '$data.contract.seats': {
      handler: function(newValue, oldValue) {
        if (newValue !== oldValue) {
          if (newValue === 'high-school') {
            this.price = 999
            this.originalPrice = this.price
          } else if (newValue === 'enterprise') {
            this.price = 2499
            this.originalPrice = this.price
          } else {
            this.price = 999
            this.originalPrice = this.price
          }
          this.handleCouponChange()
        }
      },
      immediate: true
    }
  },

  methods: {
    async serverRequest(url, config = {}) {
      return axios({
        method: config.method || "post",
        url,
        data: config.body,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": window.Spark.csrfToken,
        },
      })
        .then(checkServerError)
        .then((x) => x.data);
    },

    async setupPaymentMethod() {
      try {
        const response = await this.stripe.createPaymentMethod({
          type: 'card',
          card: this.cardElement,
          billing_details: {
            name: this.contract.billingName,
            email: this.contract.billingEmail
          }
        })

        if (response.error) {
          throw new Error(response.error)
        }

        return response.paymentMethod.id
      } catch (err) {
        console.error(err)
        throw new Error('Sorry, there was a problem with your request. Please review your payment method and try again.')
      }
    },

    handleCouponChange: debounce(async function() {
      try {
        const coupon = await this.getCoupon()
        this.couponMatch = {
          valid: coupon.valid,
          name: coupon.name || '',
          percent_off: coupon.percent_off || 0,
          amount_off: coupon.amount_off || 0,
        }

        if (coupon.valid) {
          const calcPrice = parseFloat((this.originalPrice - (coupon.percent_off ? (this.originalPrice * (coupon.percent_off / 100)) : (parseInt(coupon.amount_off) / 100) || 0)).toFixed(2));
          this.price = calcPrice < 0 ? 0 : calcPrice
        } else {
          this.price = this.originalPrice
        }
      } catch (err) {
        this.price = this.originalPrice
        this.couponMatch = {
          valid: false,
          name: '',
          percent_off: 0,
          amount_off: 0,
        }
        alert(err.message)
      }
    }, 500),

    async getCoupon() {
      this.couponLoading = true
      if (!this.coupon || !['small-business', 'enterprise', 'high-school'].includes(this.contract.seats)) {
        this.couponLoading = false
        return { valid: false }
      }

      const coupon = this.coupon.trim() === 'Marriott2425' ? 'NewMarriott2425' : this.coupon.trim()

      try {
        const response = await this.serverRequest(`/coupons/${coupon}?productId=${this.getProductId(this.contract.seats)}`, { method: 'GET' })

        if (response.error) {
          throw new Error(response.error)
        }

        this.couponLoading = false
        console.log(response)
        return response.coupon
      } catch (err) {
        this.couponLoading = false
        console.error(err)
        if (exposedCouponErrors.includes(err.message)) throw err

        throw new Error('Sorry there was a problem checking the provided coupon, please contact support at contact@thebutterbook.com for help')
      }
    },

    validateInputs() {
      if (!this.contract.name) throw new Error('Please provide your organization/property name')
      if (!this.contract.country) throw new Error('Please provide your country')
      if (!this.contract.street) throw new Error('Please provide your street address')
      if (!this.contract.city) throw new Error('Please provide your city')
      if (!this.contract.adminFname) throw new Error('Please provide the first name of the on-site administrator')
      if (!this.contract.adminLname) throw new Error('Please provide the last name of the on-site administrator')
      if (!this.contract.adminEmail) throw new Error('Please provide the email of the on-site administrator')
      if (!this.contract.adminPhone) throw new Error('Please provide the phone number of the on-site administrator')
      if (!this.contract.seats) throw new Error('Please provide the number of seats you would like to purchase')
      if (['small-business','enterprise','high-school'].includes(this.contract.seats)) {
        if (!this.contract.billingName) throw new Error('Please provide the cardholder\'s name')
        if (!this.contract.billingEmail) throw new Error('Please provide the cardholder\'s email')
        if (!this.contract.billingZipcode) throw new Error('Please provide the billing postal code')
        if (!this.contract.billingPhone) throw new Error('Please provide the cardholder\'s phone number')
      }
      if (!this.acceptTerms) throw new Error('Please accept the terms of service')
    },

    async submit() {
      try {
        this.validateInputs()

        this.loading = true
        
        const coupon = this.coupon.trim() === 'Marriott2425' ? 'NewMarriott2425' : this.coupon.trim()

        const payload = {
          contract: {
            ...this.contract,
            ...(['small-business','enterprise','high-school'].includes(this.contract.seats) ? {
              paymentMethod: await this.setupPaymentMethod()
            } : {}),
          },
          coupon: coupon,
          productId: this.getProductId(this.contract.seats),
        }

        try {
          const response = await this.serverRequest('/enterprise-api/setup', {
            method: 'POST',
            body: payload
          })

          if (response.error) throw new Error(response.error)

          this.loading = false
          this.submitted = true
        } catch (err) {
          console.error(err)
          if (exposedCouponErrors.includes(err.message)) throw err
          throw new Error('Sorry there was a problem submitting your request, please contact support at contact@thebutterbook.com for help')
        }

        window.scrollTo(0, 0)
      } catch (err) {
        alert(err.message)

        this.loading = false
      }
    },

    getProductId(seats) {
      return {
        'small-business': process.env.MIX_STRIPE_SMALL_BUSINESS_PRODUCT,
        'enterprise': process.env.MIX_STRIPE_ENTERPRISE_PRODUCT,
        'high-school': process.env.MIX_STRIPE_ENTERPRISE_SCHOOLS_PRODUCT,
      }[seats] || ''
    }
  },
}
</script>